/**
 * Job Monitoring Hook - Background Job Queue Management
 *
 * FERP CRITICAL: Real-time background job monitoring and management
 *
 * Backend: JobStatusRepository (DynamoDB with Redis caching)
 * DynamoDB: PK=T#{tenant}#JOB_STATUS, SK=JOB#{id}
 *
 * Features:
 * - Real-time job queue monitoring
 * - Job status tracking (queued, running, completed, failed)
 * - Failed job retry management
 * - Job progress tracking
 * - Queue metrics (depth, throughput, wait time)
 * - Worker utilization monitoring
 * - Error pattern analysis
 *
 * Revenue Impact: $400K/year - Enterprise job monitoring
 */

import { useState, useCallback, useEffect } from 'react';
import { useGraphQLClient } from '../../hooks/useGraphQLClient';
import { useCurrentTenant } from '../../hooks/useCurrentTenant';
import { notifications } from '@mantine/notifications';

// ============================================================
// GraphQL Queries (Backend Integration)
// ============================================================

const LIST_JOBS = `
  query ListJobStatuses($tenant: String!, $filter: JobStatusFilterInput, $pagination: PaginationInput) {
    listJobStatuses(tenant: $tenant, filter: $filter, pagination: $pagination) {
      items {
        id
        tenant
        jobType
        status
        progress
        metadata
        createdAt
      }
      total
    }
  }
`;

const GET_JOB = `
  query GetJobStatus($tenant: String!, $id: String!) {
    getJobStatus(tenant: $tenant, id: $id) {
      id
      tenant
      jobType
      status
      progress
      metadata
      createdAt
    }
  }
`;

const CREATE_JOB = `
  mutation CreateJobStatus($tenant: String!, $input: CreateJobStatusInput!) {
    createJobStatus(tenant: $tenant, input: $input) {
      id
      tenant
      jobType
      status
      progress
      metadata
      createdAt
    }
  }
`;

const UPDATE_JOB = `
  mutation UpdateJobStatus($tenant: String!, $id: String!, $input: UpdateJobStatusInput!) {
    updateJobStatus(tenant: $tenant, id: $id, input: $input) {
      id
      tenant
      jobType
      status
      progress
      metadata
      createdAt
    }
  }
`;

const RETRY_JOB = `
  mutation RetryJob($tenant: String!, $jobId: String!) {
    retryJob(tenant: $tenant, jobId: $jobId) {
      id
      status
      progress
      metadata
    }
  }
`;

const RETRY_BATCH = `
  mutation RetryJobsBatch($tenant: String!, $jobIds: [String!]!) {
    retryJobsBatch(tenant: $tenant, jobIds: $jobIds) {
      successCount
      failureCount
      errors
    }
  }
`;

const GET_QUEUE_METRICS = `
  query GetJobQueueMetrics($tenant: String!, $timeRange: TimeRangeInput) {
    getJobQueueMetrics(tenant: $tenant, timeRange: $timeRange) {
      queueDepth
      processingRate
      avgWaitTimeMs
      avgProcessingTimeMs
      throughputPerHour
      workerUtilization
      activeWorkers
      totalWorkers
      jobsByStatus {
        queued
        running
        completed
        failed
        retrying
      }
      jobsByType
      errorRate
    }
  }
`;

const GET_FAILED_JOBS = `
  query GetFailedJobs($tenant: String!, $limit: Int) {
    getFailedJobs(tenant: $tenant, limit: $limit) {
      id
      jobType
      status
      progress
      metadata
      createdAt
      failureReason
      retryCount
      lastRetryAt
    }
  }
`;

const GET_ERROR_PATTERNS = `
  query GetJobErrorPatterns($tenant: String!, $timeRange: TimeRangeInput) {
    getJobErrorPatterns(tenant: $tenant, timeRange: $timeRange) {
      errorType
      errorMessage
      occurrenceCount
      affectedJobTypes
      firstOccurrence
      lastOccurrence
      suggestion
    }
  }
`;

// ============================================================
// TypeScript Types (Backend Alignment)
// ============================================================

export type JobStatusType =
  | 'queued'
  | 'running'
  | 'completed'
  | 'failed'
  | 'retrying'
  | 'cancelled'
  | 'timeout';

export type JobType =
  | 'workflow_execution'
  | 'template_validation'
  | 'chaos_ingestion'
  | 'data_migration'
  | 'report_generation'
  | 'email_notification'
  | 'backup'
  | 'cleanup'
  | 'optimization_analysis'
  | 'compliance_scan';

export interface JobStatus {
  id: string;
  tenant: string;
  jobType: JobType;
  status: JobStatusType;
  progress: number;
  metadata: any;
  createdAt: string;
  failureReason?: string;
  retryCount?: number;
  lastRetryAt?: string;
}

export interface JobStatusFilter {
  jobType?: JobType;
  status?: JobStatusType;
}

export interface QueueMetrics {
  queueDepth: number;
  processingRate: number;
  avgWaitTimeMs: number;
  avgProcessingTimeMs: number;
  throughputPerHour: number;
  workerUtilization: number;
  activeWorkers: number;
  totalWorkers: number;
  jobsByStatus: {
    queued: number;
    running: number;
    completed: number;
    failed: number;
    retrying: number;
  };
  jobsByType: Record<JobType, number>;
  errorRate: number;
}

export interface ErrorPattern {
  errorType: string;
  errorMessage: string;
  occurrenceCount: number;
  affectedJobTypes: JobType[];
  firstOccurrence: string;
  lastOccurrence: string;
  suggestion: string;
}

export interface RetryBatchResult {
  successCount: number;
  failureCount: number;
  errors: string[];
}

export interface TimeRange {
  startTime: string;
  endTime: string;
}

// ============================================================
// Hook Implementation
// ============================================================

export function useJobMonitoring() {
  const client = useGraphQLClient();
  const { tenantId } = useCurrentTenant();

  const [jobs, setJobs] = useState<JobStatus[]>([]);
  const [selectedJob, setSelectedJob] = useState<JobStatus | null>(null);
  const [metrics, setMetrics] = useState<QueueMetrics | null>(null);
  const [errorPatterns, setErrorPatterns] = useState<ErrorPattern[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  /**
   * List jobs with optional filter
   */
  const listJobs = useCallback(async (
    filter?: JobStatusFilter
  ): Promise<JobStatus[]> => {
    if (!tenantId) {
      throw new Error('No tenant context available');
    }

    setLoading(true);
    setError(null);

    try {
      const result = await client.query({
        query: LIST_JOBS,
        variables: { tenant: tenantId, filter }
      });

      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }

      const jobsList = result.data?.listJobStatuses.items;
      setJobs(jobsList);

      return jobsList;

    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to list jobs');
      setError(error);
      throw error;

    } finally {
      setLoading(false);
    }
  }, [client, tenantId]);

  /**
   * Get single job status
   */
  const getJob = useCallback(async (jobId: string): Promise<JobStatus> => {
    if (!tenantId) {
      throw new Error('No tenant context available');
    }

    try {
      const result = await client.query({
        query: GET_JOB,
        variables: { tenant: tenantId, id: jobId }
      });

      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }

      const job = result.data?.getJobStatus;
      setSelectedJob(job);

      return job;

    } catch (err) {
      throw err instanceof Error ? err : new Error('Failed to get job');
    }
  }, [client, tenantId]);

  /**
   * Create new job
   */
  const createJob = useCallback(async (
    jobType: JobType,
    status: JobStatusType = 'queued'
  ): Promise<JobStatus> => {
    if (!tenantId) {
      throw new Error('No tenant context available');
    }

    try {
      const result = await client.mutate({
        mutation: CREATE_JOB,
        variables: {
          tenant: tenantId,
          input: { jobType, status }
        }
      });

      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }

      const newJob = result.data?.createJobStatus;

      // Refresh job list
      await listJobs();

      notifications.show({
        title: 'Job Created',
        message: `Job ${jobType} created successfully`,
        color: 'green'
      });

      return newJob;

    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to create job');
      notifications.show({
        title: 'Error',
        message: error.message,
        color: 'red'
      });
      throw error;
    }
  }, [client, tenantId, listJobs]);

  /**
   * Update job status
   */
  const updateJob = useCallback(async (
    jobId: string,
    status?: JobStatusType,
    progress?: number
  ): Promise<JobStatus> => {
    if (!tenantId) {
      throw new Error('No tenant context available');
    }

    try {
      const result = await client.mutate({
        mutation: UPDATE_JOB,
        variables: {
          tenant: tenantId,
          id: jobId,
          input: { status, progress }
        }
      });

      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }

      const updatedJob = result.data?.updateJobStatus;

      // Refresh job list
      await listJobs();

      return updatedJob;

    } catch (err) {
      throw err instanceof Error ? err : new Error('Failed to update job');
    }
  }, [client, tenantId, listJobs]);

  /**
   * Retry failed job
   */
  const retryJob = useCallback(async (jobId: string): Promise<JobStatus> => {
    if (!tenantId) {
      throw new Error('No tenant context available');
    }

    try {
      const result = await client.mutate({
        mutation: RETRY_JOB,
        variables: { tenant: tenantId, jobId }
      });

      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }

      const retriedJob = result.data?.retryJob;

      // Refresh job list
      await listJobs();

      notifications.show({
        title: 'Job Retried',
        message: 'Job has been queued for retry',
        color: 'blue'
      });

      return retriedJob;

    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to retry job');
      notifications.show({
        title: 'Error',
        message: error.message,
        color: 'red'
      });
      throw error;
    }
  }, [client, tenantId, listJobs]);

  /**
   * Retry multiple failed jobs
   */
  const retryJobsBatch = useCallback(async (
    jobIds: string[]
  ): Promise<RetryBatchResult> => {
    if (!tenantId) {
      throw new Error('No tenant context available');
    }

    try {
      const result = await client.mutate({
        mutation: RETRY_BATCH,
        variables: { tenant: tenantId, jobIds }
      });

      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }

      const batchResult = result.data?.retryJobsBatch;

      // Refresh job list
      await listJobs();

      notifications.show({
        title: 'Batch Retry Complete',
        message: `${batchResult.successCount} jobs retried, ${batchResult.failureCount} failed`,
        color: batchResult.failureCount === 0 ? 'green' : 'orange'
      });

      return batchResult;

    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to retry jobs');
      notifications.show({
        title: 'Error',
        message: error.message,
        color: 'red'
      });
      throw error;
    }
  }, [client, tenantId, listJobs]);

  /**
   * Get queue metrics
   */
  const fetchMetrics = useCallback(async (
    timeRange?: TimeRange
  ): Promise<QueueMetrics> => {
    if (!tenantId) {
      throw new Error('No tenant context available');
    }

    try {
      const result = await client.query({
        query: GET_QUEUE_METRICS,
        variables: { tenant: tenantId, timeRange }
      });

      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }

      const metricsData = result.data?.getJobQueueMetrics;
      setMetrics(metricsData);

      return metricsData;

    } catch (err) {
      throw err instanceof Error ? err : new Error('Failed to fetch metrics');
    }
  }, [client, tenantId]);

  /**
   * Get failed jobs
   */
  const getFailedJobs = useCallback(async (limit: number = 50): Promise<JobStatus[]> => {
    if (!tenantId) {
      throw new Error('No tenant context available');
    }

    try {
      const result = await client.query({
        query: GET_FAILED_JOBS,
        variables: { tenant: tenantId, limit }
      });

      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }

      return result.data?.getFailedJobs ?? null;

    } catch (err) {
      throw err instanceof Error ? err : new Error('Failed to get failed jobs');
    }
  }, [client, tenantId]);

  /**
   * Get error patterns
   */
  const fetchErrorPatterns = useCallback(async (
    timeRange?: TimeRange
  ): Promise<ErrorPattern[]> => {
    if (!tenantId) {
      throw new Error('No tenant context available');
    }

    try {
      const result = await client.query({
        query: GET_ERROR_PATTERNS,
        variables: { tenant: tenantId, timeRange }
      });

      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message);
      }

      const patterns = result.data?.getJobErrorPatterns;
      setErrorPatterns(patterns);

      return patterns;

    } catch (err) {
      throw err instanceof Error ? err : new Error('Failed to fetch error patterns');
    }
  }, [client, tenantId]);

  /**
   * Get jobs by status
   */
  const getJobsByStatus = useCallback((status: JobStatusType): JobStatus[] => {
    return jobs.filter(job => job.status === status);
  }, [jobs]);

  /**
   * Get jobs by type
   */
  const getJobsByType = useCallback((jobType: JobType): JobStatus[] => {
    return jobs.filter(job => job.jobType === jobType);
  }, [jobs]);

  /**
   * Calculate average processing time
   */
  const calculateAvgProcessingTime = useCallback((): number => {
    if (!metrics) return 0;
    return metrics.avgProcessingTimeMs;
  }, [metrics]);

  /**
   * Format duration for display
   */
  const formatDuration = useCallback((ms: number): string => {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    if (ms < 3600000) return `${(ms / 60000).toFixed(1)}m`;
    return `${(ms / 3600000).toFixed(1)}h`;
  }, []);

  /**
   * Auto-refresh data (every 5 seconds for real-time monitoring)
   */
  useEffect(() => {
    if (!tenantId) return undefined;

    listJobs();
    fetchMetrics();
    fetchErrorPatterns();

    const interval = setInterval(() => {
      listJobs();
      fetchMetrics();
    }, 5000); // 5 second refresh

    return () => clearInterval(interval);
  }, [tenantId, listJobs, fetchMetrics, fetchErrorPatterns]);

  return {
    // State
    jobs,
    selectedJob,
    metrics,
    errorPatterns,
    loading,
    error,

    // Actions
    listJobs,
    getJob,
    createJob,
    updateJob,
    retryJob,
    retryJobsBatch,
    fetchMetrics,
    getFailedJobs,
    fetchErrorPatterns,

    // Selection
    setSelectedJob,

    // Helpers
    getJobsByStatus,
    getJobsByType,
    calculateAvgProcessingTime,
    formatDuration
  };
}
